'use client';

import type React from 'react';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';

import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import { MORE_ABOUT_MOVIE_CTA_FLAG_KEY } from '@/constants/ab-test';

import { IconRightArrow16 } from '@cineamo/ui/src/icons/ic-rightArrow';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

type MoreAboutMovieCtaProps = {
    /** Fully-built embed URL (incl. UTM params), computed server-side. */
    href: string;
    /** Resolved variant wording to render. */
    label: string;
    /** A/B variant id (see ab-test.ts) — the analytics dimension we compare on. */
    variant: string;
    /** Movie id, for slicing the funnel per movie. */
    movieId: MovieDto['id'];
    primaryColor: string;
};

/**
 * CPLA-44 — client wrapper around the "More about movie" CTA. Fires the "clicked" event
 * (the CTR numerator) on navigation. The denominator is the pageview itself: the page
 * emits the resolved variant to the DOM via <FlagValues>, so Vercel Web Analytics tags
 * both the pageview and this click event with the `more-about-movie-cta` flag dimension.
 */
const MoreAboutMovieCta: React.FC<MoreAboutMovieCtaProps> = ({ href, label, variant, movieId, primaryColor }) => {
    const { track } = useAnalytics();

    return (
        <a
            href={href}
            target="_self"
            onClick={() =>
                track(
                    'More About Movie Clicked',
                    { variant, movieId: movieId ?? '' },
                    { flags: [MORE_ABOUT_MOVIE_CTA_FLAG_KEY] }
                )
            }>
            <Button
                labelClassName="text-body-bold-12"
                style={ButtonStyle.text_only_no_padding}
                css={{ color: primaryColor }}
                trailingIcon={<IconRightArrow16 className="flex-none" />}>
                {label}
            </Button>
        </a>
    );
};

export default MoreAboutMovieCta;
